import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { NewSideBarMagasins } from "../../NewSidebarMagasin";
import { formatCodeDevis } from '../../../Services/CodeTrackingHelper';

import {
  FileText,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  XSquare,
  Search,
  Wallet,
  ListChecks,
  PlusCircle,
} from 'lucide-react';
import './GestionDevis.css';

interface DevisArticle {
  id: number;
  DevisId: string;
  RefDevis?: string;
  client: string;
  Idclient: string;
  Idmagasin: string;
  RefArticle: string;
  designation: string;
  qte: number;
  prix: string;
  status: string;
  total: string;
  date: string;
  status_payment?: string;
  client_adresse?: string;
}

interface GroupedDevis {
  DevisId: string;
  RefDevis?: string;
  firstId?: number;
  client: string;
  date: string;
  status: string;
  totalDevis: number;
  articles: DevisArticle[];
  Idclient?: string;
  Idmagasin?: string;
  status_payment?: string;
  client_adresse?: string;
}


type StatusFilter = 'tous' | 'en_attente' | 'accepte' | 'refuse' | 'annule';

const classifyStatus = (status: string): Exclude<StatusFilter, 'tous'> => {
  const s = (status || '').toLowerCase();
  if (s.includes('accept') || s.includes('confirm')) return 'accepte';
  if (s.includes('refus')) return 'refuse';
  if (s.includes('annul')) return 'annule';
  return 'en_attente';
};

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'tous', label: 'Tous les statuts' },
  { value: 'en_attente', label: 'En attente' },
  { value: 'accepte', label: 'Accepté' },
  { value: 'refuse', label: 'Refusé' },
  { value: 'annule', label: 'Annulé' },
];

const GestionDevis: React.FC = () => {
  const MagasinId = localStorage.getItem("MagasinId");
  const [groupedDevis, setGroupedDevis] = useState<GroupedDevis[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevis, setSelectedDevis] = useState<GroupedDevis | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('tous');

  useEffect(() => {
    fetchDevis();
  }, []);

  const fetchDevis = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_PHP_APP_URL}/magasin/${MagasinId}/devis`);
      const devisData: DevisArticle[] = res.data;

      // Group by DevisId
      const grouped = devisData.reduce((acc: Record<string, GroupedDevis>, curr) => {
        if (!acc[curr.DevisId]) {
          acc[curr.DevisId] = {
            DevisId: curr.DevisId,
            RefDevis: curr.RefDevis,
            firstId: curr.id,
            client: curr.client,
            date: new Date(curr.date).toLocaleDateString('fr-FR', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            }),
            status: curr.status,
            totalDevis: 0,
            articles: [],
            Idclient: curr.Idclient,
            Idmagasin: curr.Idmagasin,
            status_payment: curr.status_payment,
            client_adresse: curr.client_adresse,
          };
        }
        acc[curr.DevisId].articles.push(curr);
        acc[curr.DevisId].totalDevis += parseFloat(curr.prix) * curr.qte;
        return acc;
      }, {});

      setGroupedDevis(Object.values(grouped).sort((a, b) => new Date(b.articles[0].date).getTime() - new Date(a.articles[0].date).getTime()));
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const category = classifyStatus(status);
    if (category === 'accepte') {
      return <span className="status-badge status-accepted"><CheckCircle2 size={14} /> Accepté</span>;
    }
    if (category === 'refuse') {
      return <span className="status-badge status-refused"><XCircle size={14} /> Refusé</span>;
    }
    if (category === 'annule') {
      return <span className="status-badge status-cancelled"><XSquare size={14} /> Annulé</span>;
    }
    return <span className="status-badge status-pending"><Clock size={14} /> {status || 'En attente'}</span>;
  };

  const handleAnnuler = async (devisId: string) => {
    if (!window.confirm('Voulez-vous vraiment annuler ce devis ?')) return;
    try {
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/magasin/devis/${devisId}/annuler`);
      fetchDevis();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'annulation');
    }
  };

  const handleSupprimer = async (devisId: string) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce devis ?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_PHP_APP_URL}/magasin/devis/${devisId}/supprimer`);
      fetchDevis();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la suppression');
    }
  };

  const stats = useMemo(() => {
    const total = groupedDevis.length;
    const enAttente = groupedDevis.filter((d) => classifyStatus(d.status) === 'en_attente').length;
    const acceptes = groupedDevis.filter((d) => classifyStatus(d.status) === 'accepte').length;
    const montantTotal = groupedDevis.reduce((sum, d) => sum + d.totalDevis, 0);
    return { total, enAttente, acceptes, montantTotal };
  }, [groupedDevis]);

  const filteredDevis = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groupedDevis.filter((d) => {
      const matchesSearch = !q || d.DevisId.toLowerCase().includes(q) || d.client.toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'tous' || classifyStatus(d.status) === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [groupedDevis, search, statusFilter]);

  return (
    <>
      <NewSideBarMagasins />
      <div className="gestion-devis-container">
        <div className="devis-header">
          <div className="devis-header-content">
            <div className="devis-header-icon">
              <FileText size={40} strokeWidth={2} />
            </div>
            <div className="devis-header-text">
              <h1 className="devis-title">Gestion des Devis</h1>
              <p className="devis-subtitle">Consultez et suivez l'état de vos devis</p>
            </div>
          </div>
          <div className="devis-header-decoration"></div>
        </div>

        <div className="devis-stats-grid">
          <div className={`devis-stat-card ${loading ? 'shimmer' : ''}`}>
            {!loading && (
              <>
                <div className="devis-stat-icon stat-icon-total"><ListChecks size={20} /></div>
                <div className="devis-stat-text">
                  <span className="devis-stat-value">{stats.total}</span>
                  <span className="devis-stat-label">Devis au total</span>
                </div>
              </>
            )}
          </div>
          <div className={`devis-stat-card ${loading ? 'shimmer' : ''}`}>
            {!loading && (
              <>
                <div className="devis-stat-icon stat-icon-pending"><Clock size={20} /></div>
                <div className="devis-stat-text">
                  <span className="devis-stat-value">{stats.enAttente}</span>
                  <span className="devis-stat-label">En attente</span>
                </div>
              </>
            )}
          </div>
          <div className={`devis-stat-card ${loading ? 'shimmer' : ''}`}>
            {!loading && (
              <>
                <div className="devis-stat-icon stat-icon-accepted"><CheckCircle2 size={20} /></div>
                <div className="devis-stat-text">
                  <span className="devis-stat-value">{stats.acceptes}</span>
                  <span className="devis-stat-label">Acceptés</span>
                </div>
              </>
            )}
          </div>
          <div className={`devis-stat-card ${loading ? 'shimmer' : ''}`}>
            {!loading && (
              <>
                <div className="devis-stat-icon stat-icon-amount"><Wallet size={20} /></div>
                <div className="devis-stat-text">
                  <span className="devis-stat-value">{stats.montantTotal.toFixed(2)} MAD</span>
                  <span className="devis-stat-label">Montant total</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="devis-toolbar">
          <div className="devis-search-wrapper">
            <Search size={16} />
            <input
              type="text"
              placeholder="Rechercher par client ou n° devis..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="devis-search-input"
            />
          </div>
          <div className="devis-toolbar-right">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="devis-status-filter"
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <Link to={`/magasin/${MagasinId}/devis`} className="add-devis-btn">
              <PlusCircle size={17} />
              Ajouter devis
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="devis-list-container">
            <div className="devis-skeleton-list">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="devis-skeleton-row shimmer" style={{ animationDelay: `${i * 0.05}s` }} />
              ))}
            </div>
          </div>
        ) : (
          <div className="devis-list-container">
            {filteredDevis.length === 0 ? (
              <div className="no-devis-message">
                <FileText size={36} />
                <p>{groupedDevis.length === 0 ? 'Aucun devis trouvé.' : 'Aucun devis ne correspond à votre recherche.'}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="devis-table">
                  <thead>
                    <tr>
                      <th>N° Devis</th>
                      <th>Client</th>
                      <th>Date</th>
                      <th>Total HT</th>
                      <th>Statut</th>
                      <th>Paiement / Adresse Client</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDevis.map((devis) => (
                      <React.Fragment key={devis.DevisId}>
                        <tr className={selectedDevis?.DevisId === devis.DevisId ? 'row-expanded' : ''}>
                          <td><strong className="gd-tracking-code">{formatCodeDevis(devis.RefDevis, devis.firstId, devis.articles[0]?.date)}</strong></td>
                          <td>{devis.client}</td>
                          <td>{devis.date}</td>
                          <td className="cell-amount">{devis.totalDevis.toFixed(2)} MAD</td>
                          <td>{getStatusBadge(devis.status)}</td>
                          <td>
                            {String(devis.Idclient) === String(MagasinId) ? (
                              <span className={`status-badge ${devis.status_payment === 'payé' ? 'status-accepted' : 'status-refused'}`} style={{ display: 'inline-flex', padding: '3px 8px', fontSize: '0.75rem' }}>
                                {devis.status_payment === 'payé' ? 'Payé' : 'Non payé'}
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                {devis.client_adresse || 'Non spécifiée'}
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="action-buttons-group">
                              <button
                                className="action-btn view-devis-btn"
                                onClick={() => setSelectedDevis(selectedDevis?.DevisId === devis.DevisId ? null : devis)}
                                title="Détails"
                              >
                                <Eye size={16} /> {selectedDevis?.DevisId === devis.DevisId ? 'Fermer' : 'Détails'}
                              </button>
                              {devis.status !== 'annulé' && devis.status !== 'refusé' && (
                                <button
                                  className="action-btn cancel-devis-btn"
                                  onClick={() => handleAnnuler(devis.DevisId)}
                                  title="Annuler"
                                >
                                  <XSquare size={16} /> Annuler
                                </button>
                              )}
                              <button
                                className="action-btn delete-devis-btn"
                                onClick={() => handleSupprimer(devis.DevisId)}
                                title="Supprimer"
                              >
                                <Trash2 size={16} /> Supprimer
                              </button>
                            </div>
                          </td>
                        </tr>
                        {selectedDevis?.DevisId === devis.DevisId && (
                          <tr className="devis-details-row">
                            <td colSpan={7}>
                              <div className="devis-details-box">
                                <h4>Détails des articles</h4>
                                <table className="articles-table">
                                  <thead>
                                    <tr>
                                      <th>Réf</th>
                                      <th>Désignation</th>
                                      <th>Qté</th>
                                      <th>Prix U. HT</th>
                                      <th>Total HT</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {devis.articles.map(art => (
                                      <tr key={art.id}>
                                        <td>{art.RefArticle}</td>
                                        <td>{art.designation}</td>
                                        <td>{art.qte}</td>
                                        <td>{parseFloat(art.prix).toFixed(2)} MAD</td>
                                        <td>{(parseFloat(art.prix) * art.qte).toFixed(2)} MAD</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default GestionDevis;